import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, AlertTriangle, RefreshCw, Activity, HardDrive, Zap, Volume2, Package } from 'lucide-react';
import { PLATFORM_VERSION } from '../../config/version';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.5.803 — Deploy Health Dashboard
// One-glance visibility into whether a deploy actually shipped what it says
// it shipped. Super-admin only (route-guarded). Answers the question:
//   "Did my Save-to-GitHub → Plesk deploy just work, or did it silently ship
//    a stale build again?"
//
// Every row shows:
//   • The metric value
//   • Green ✅ or red ⚠ based on expected thresholds
//   • Actionable copy for common failure modes
export default function DeployHealthPage() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [backendVersion, setBackendVersion] = useState(null);
  const [deepHealth, setDeepHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, v, d] = await Promise.allSettled([
        axios.get(`${API}/api/settings/deploy-health`),
        axios.get(`${API}/api/version`),
        axios.get(`${API}/api/health/deep`),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      if (v.status === 'fulfilled') setBackendVersion(v.value.data);
      if (d.status === 'fulfilled') setDeepHealth(d.value.data);
      setLastCheck(new Date().toLocaleTimeString());
    } catch (e) {
      // Access denied → will be handled by the "super_admin only" guard below
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') load();
  }, [user]);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6" data-testid="deploy-health-access-denied">
        <Card>
          <CardContent className="p-6 text-center text-gray-600">
            Deploy Health Dashboard is available to Platform Owners only.
          </CardContent>
        </Card>
      </div>
    );
  }

  // v2.5.872 — Compute backend restart age. If backend has been running for
  // more than 24h, the deployed Python code is likely stale even though
  // /api/version reports the latest string (that endpoint reads the frontend
  // config file, not the actually-running route code). This is the exact
  // failure mode behind "Not Found" toasts on new endpoints in production.
  const backendAgeHours = (() => {
    const startedIso = backendVersion?.backend_started_at;
    if (!startedIso) return null;
    const started = new Date(startedIso).getTime();
    if (isNaN(started)) return null;
    return (Date.now() - started) / (1000 * 60 * 60);
  })();
  const backendStale = backendAgeHours !== null && backendAgeHours > 24;

  const rows = [
    {
      icon: Activity,
      label: 'Backend health',
      value: backendVersion?.healthy ? 'Healthy' : 'Unreachable',
      ok: !!backendVersion?.healthy,
      hint: backendVersion?.healthy
        ? `Started ${backendVersion.backend_started_at?.slice(0, 19) || 'unknown'}`
        : 'Backend /api/version did not respond. Check supervisor / uvicorn logs.',
    },
    {
      icon: Activity,
      label: 'Backend code freshness',
      value: backendAgeHours === null
        ? 'Unknown'
        : backendAgeHours < 1
          ? 'Just restarted'
          : backendAgeHours < 24
            ? `Restarted ${backendAgeHours.toFixed(1)}h ago`
            : `${(backendAgeHours / 24).toFixed(1)} DAYS since restart`,
      ok: !backendStale,
      hint: backendStale
        ? 'CRITICAL: Backend Python process has NOT been restarted since the last deploy. New API routes will 404 even though /api/version reports the latest string. SSH into your server and run: `sudo systemctl restart konnect-backend` OR run `bash /var/www/vhosts/kaharian.com/konnect.kaharian.com/deploy.sh`'
        : 'Backend is running fresh code — new endpoints available.',
    },
    {
      icon: HardDrive,
      label: 'Frontend build present',
      value: health?.frontend_build_healthy
        ? `${(health.frontend_build_size_bytes / 1024).toFixed(1)} KB index.html`
        : 'Missing / stale',
      ok: !!health?.frontend_build_healthy,
      hint: health?.frontend_build_healthy
        ? 'Compiled bundle detected on disk'
        : 'Run "cd frontend && yarn build" then commit + push.',
    },
    {
      icon: Package,
      label: 'Automation templates loaded',
      value: `${health?.automation_templates_loaded ?? '?'} / ${health?.automation_templates_expected ?? 60}`,
      ok: !!health?.automation_healthy,
      hint: health?.automation_healthy
        ? 'All templates registered'
        : 'Some templates missing — backend may be running stale code.',
    },
    {
      icon: Volume2,
      label: 'Tour audio clips on disk',
      value: `${health?.tour_audio_clips ?? '?'} / ${health?.tour_audio_expected ?? 65}`,
      ok: !!health?.tour_audio_healthy,
      hint: health?.tour_audio_healthy
        ? 'Static MP3 pipeline healthy — replays cost $0'
        : 'Missing clips — call POST /api/tour/generate-all-missing or restart backend.',
    },
    {
      icon: Zap,
      label: 'Version alignment',
      value: `Frontend ${PLATFORM_VERSION} · Backend ${backendVersion?.version || '?'}`,
      ok: !!backendVersion,
      hint: backendVersion
        ? `Both signals reachable — sidebar drift indicator reflects this. Backend reads version from: ${backendVersion?.version_source || 'unknown source'}`
        : 'Backend version endpoint unreachable',
    },
    // v2.5.875 — Deep health rows (from /api/health/deep)
    {
      icon: Activity,
      label: 'FastAPI route registration',
      value: deepHealth?.checks?.routes
        ? `${deepHealth.checks.routes.total} routes · ${(deepHealth.checks.routes.missing_expected || []).length} expected missing`
        : 'Unknown',
      ok: !!deepHealth?.checks?.routes?.healthy,
      hint: deepHealth?.checks?.routes?.missing_expected?.length > 0
        ? `CRITICAL: expected endpoints missing → ${deepHealth.checks.routes.missing_expected.join(', ')}. Restart the backend.`
        : 'All expected endpoints are registered.',
    },
    {
      icon: HardDrive,
      label: 'Static/upload dirs writable',
      value: deepHealth?.checks?.filesystem?.dirs
        ? `${deepHealth.checks.filesystem.dirs.filter(d => d.writable).length} / ${deepHealth.checks.filesystem.dirs.length} writable`
        : 'Unknown',
      ok: !!deepHealth?.checks?.filesystem?.healthy,
      hint: deepHealth?.checks?.filesystem?.dirs
        ? deepHealth.checks.filesystem.dirs.filter(d => !d.writable).map(d => `${d.label}: ${d.path}`).join(' · ') || 'All upload/static dirs are writable — no PermissionError risk.'
        : '',
    },
    {
      icon: Package,
      label: 'MongoDB',
      value: deepHealth?.checks?.mongodb
        ? `${deepHealth.checks.mongodb.collection_count} collections · ${deepHealth.checks.mongodb.ping_ms}ms ping`
        : 'Unknown',
      ok: !!deepHealth?.checks?.mongodb?.healthy,
      hint: deepHealth?.checks?.mongodb?.error || 'Database reachable.',
    },
    {
      icon: HardDrive,
      label: 'Frontend bundle',
      value: deepHealth?.checks?.frontend_bundle?.found_bundles?.[0]
        ? `${deepHealth.checks.frontend_bundle.found_bundles[0].size_kb} KB · v${(deepHealth.checks.frontend_bundle.found_bundles[0].versions_in_bundle || []).slice(-1)[0] || '?'}`
        : 'Not found',
      ok: !!deepHealth?.checks?.frontend_bundle?.healthy,
      hint: deepHealth?.checks?.frontend_bundle?.found_bundles?.[0]?.path || 'No compiled bundle detected on disk.',
    },
  ];

  const overallOk = health?.overall_healthy && backendVersion?.healthy;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="deploy-health-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
            Deploy Health
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            One-glance verification that your latest deploy actually shipped.
            {lastCheck && <span className="ml-1">· Last check {lastCheck}</span>}
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm"
          data-testid="deploy-health-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Checking…' : 'Refresh'}
        </Button>
      </div>

      {/* Big overall status banner */}
      <Card className={overallOk
        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 border-0'
        : 'bg-gradient-to-r from-amber-500 to-amber-600 border-0'}>
        <CardContent className="p-6 flex items-center gap-4">
          {overallOk
            ? <CheckCircle2 className="h-10 w-10 text-white" />
            : <AlertTriangle className="h-10 w-10 text-white" />}
          <div className="flex-1">
            <p className="text-white font-heading font-bold text-xl">
              {overallOk ? 'All systems go' : 'Attention needed'}
            </p>
            <p className="text-white/80 text-sm">
              {overallOk
                ? 'Your production deploy is healthy across all checks.'
                : 'One or more checks failed. Review the rows below for the fix.'}
            </p>
          </div>
          <Badge className="bg-white/20 text-white border-0" data-testid="deploy-health-badge">
            v{PLATFORM_VERSION}
          </Badge>
        </CardContent>
      </Card>

      {/* Individual health rows */}
      <Card>
        <CardHeader><CardTitle className="text-base">System Checks</CardTitle></CardHeader>
        <CardContent className="divide-y divide-gray-100 p-0">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.label} className="flex items-start gap-4 p-4"
                data-testid={`deploy-check-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                  ${r.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{r.label}</p>
                    {r.ok
                      ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">OK</Badge>
                      : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">CHECK</Badge>}
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">{r.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{r.hint}</p>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Deep dive — raw JSON for the paranoid */}
      <details className="text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700 font-medium">
          Raw health payload (JSON)
        </summary>
        <pre className="mt-2 p-3 bg-gray-50 rounded overflow-auto">
          {JSON.stringify({ health, backend: backendVersion }, null, 2)}
        </pre>
      </details>
    </div>
  );
}
